import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import "./Landing.css";

function ParticleBrain() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animId, t = 0, assembleProgress = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const N = 180;
    const particles = Array.from({ length: N }, (_, i) => {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / N);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      return {
        phi, theta,
        r: 0.85 + Math.random() * 0.2,
        speed: 0.001 + Math.random() * 0.002,
        offset: Math.random() * Math.PI * 2,
        size: 2 + Math.random() * 2.5,
        startX: (Math.random() - 0.5) * 800,
        startY: (Math.random() - 0.5) * 600,
      };
    });
    const connections = [];
    for (let i = 0; i < N; i++)
      for (let j = i + 1; j < N; j++)
        if (Math.random() < 0.025) connections.push([i, j]);

    const draw = () => {
      const W = canvas.width, H = canvas.height;
      const cx = W / 2, cy = H / 2;
      const R = Math.min(W, H) * 0.4;
      ctx.clearRect(0, 0, W, H);
      t += 0.006;
      assembleProgress = Math.min(1, assembleProgress + 0.008);
      const ease = 1 - Math.pow(1 - assembleProgress, 3);

      const pts = particles.map((p) => {
        const phi = p.phi + Math.sin(t * p.speed * 8 + p.offset) * 0.04;
        const theta = p.theta + t * p.speed * 15;
        const sinPhi = Math.sin(phi);
        const tx = cx + R * p.r * sinPhi * Math.cos(theta);
        const ty = cy + R * p.r * sinPhi * Math.sin(theta) * 0.6;
        const z = R * p.r * Math.cos(phi);
        const x = cx + p.startX + (tx - cx - p.startX) * ease;
        const y = cy + p.startY + (ty - cy - p.startY) * ease;
        const depth = (z / R + 1) / 2;
        return { x, y, depth };
      });

      if (assembleProgress > 0.3) {
        connections.forEach(([i, j]) => {
          const a = pts[i], b = pts[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist > R * 0.65) return;
          const alpha = ((1 - dist / (R * 0.65)) * (a.depth + b.depth)) / 2;
          const connAlpha = alpha * Math.min(1, (assembleProgress - 0.3) * 3) * 0.5;
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(0, 210, 180, ${connAlpha})`;
          ctx.lineWidth = 0.7; ctx.stroke();
        });
      }

      pts.forEach((p, i) => {
        const { x, y, depth } = p;
        const s = particles[i].size * (0.4 + depth * 0.9);
        const alpha = (0.5 + depth * 0.5) * Math.min(1, assembleProgress * 2);
        const hue = 165 + depth * 40;
        const light = 55 + depth * 20;
        ctx.beginPath(); ctx.arc(x, y, s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue}, 90%, ${light}%, ${alpha})`;
        ctx.fill();
        if (depth > 0.7) {
          ctx.beginPath(); ctx.arc(x, y, s * 2, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${hue}, 90%, ${light}%, ${alpha * 0.15})`;
          ctx.fill();
        }
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="brain-canvas" />;
}

function LoginModal({ onClose }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-logo">
          <span className="modal-logo-icon">⬡</span>
          <span className="modal-logo-text">THOTH AI</span>
        </div>
        <h2 className="modal-title">Вход в систему</h2>
        <p className="modal-sub">Доступ только для авторизованных пользователей</p>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="field-group">
            <label className="field-label">EMAIL</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@thoth-ai.ru" className="field-input" required />
          </div>
          <div className="field-group">
            <label className="field-label">ПАРОЛЬ</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="field-input" required />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn-login" disabled={loading}>{loading ? "Вход..." : "Войти"}</button>
        </form>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: "◈", title: "Мониторинг отчётов", desc: "Агент проверяет поступление ежедневных отчётов от сотрудников и уведомляет руководство о нарушениях в режиме реального времени.", status: "live" },
  { icon: "⟳", title: "Сверка с контрагентами", desc: "Автоматическая сверка данных TravelLine и Booking.com. Расхождения выявляются до того, как они станут проблемой.", status: "dev" },
  { icon: "◎", title: "P&L анализ", desc: "Загрузите банковскую выписку — агент категоризирует транзакции и сформирует полный отчёт о прибылях и убытках.", status: "dev" },
  { icon: "⬡", title: "Контроль задач", desc: "Руководитель ставит задачу — агент проверяет её выполнение в отчёте сотрудника через анализ текста с помощью LLM.", status: "soon" },
  { icon: "◐", title: "Мониторинг чатов", desc: "Агент следит за корпоративными мессенджерами, извлекает задачи и эскалирует важные сообщения нужным людям.", status: "soon" },
  { icon: "⊛", title: "Аналитика дисциплины", desc: "Статистика опозданий по каждому сотруднику. Учёт выходных, отпусков и исключений. История за любой период.", status: "soon" },
];

const STATUS_MAP = { live: { label: "LIVE", cls: "s-live" }, dev: { label: "В РАЗРАБОТКЕ", cls: "s-dev" }, soon: { label: "СКОРО", cls: "s-soon" } };
const STATS = [
  { value: "28", label: "Объектов автоматизировано" },
  { value: "6+", label: "Активных агентов" },
  { value: "2×", label: "Проверок в день" },
  { value: "0", label: "Ручного труда" },
];

export default function Landing() {
  const [showLogin, setShowLogin] = useState(false);
  return (
    <div className="landing">
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      <nav className="nav">
        <div className="nav-logo">
          <span className="nav-logo-icon">⬡</span>
          <span className="nav-logo-text">THOTH AI</span>
        </div>
        <div className="nav-links">
          <a href="#features" className="nav-link">Агенты</a>
          <a href="#how" className="nav-link">Как работает</a>
          <a href="#stats" className="nav-link">Результаты</a>
        </div>
        <button className="btn-nav-login" onClick={() => setShowLogin(true)}>Войти в систему →</button>
      </nav>

      <section className="hero">
        <div className="hero-content">
          <div className="hero-eyebrow">ИСКУССТВЕННЫЙ ИНТЕЛЛЕКТ ДЛЯ БИЗНЕСА</div>
          <h1 className="hero-title">Автоматизируем<br /><span className="hero-accent">всё, что можно</span><br />автоматизировать</h1>
          <p className="hero-desc">Thoth AI заменяет ручной труд автономными агентами. Мониторинг, сверки, отчёты, контроль задач — работают сами, пока ваши сотрудники занимаются тем, что действительно важно.</p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => setShowLogin(true)}>Войти в платформу</button>
            <a href="#features" className="btn-ghost">Смотреть агентов ↓</a>
          </div>
        </div>
        <div className="hero-visual">
          <ParticleBrain />
          <div className="brain-label">THOTH NEURAL CORE</div>
        </div>
      </section>

      <section className="stats-strip" id="stats">
        {STATS.map((s) => (
          <div key={s.label} className="stat-item">
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      <section className="features" id="features">
        <div className="section-eyebrow">АГЕНТЫ ПЛАТФОРМЫ</div>
        <h2 className="section-title">Каждый агент — отдельная задача</h2>
        <p className="section-sub">Модульная архитектура позволяет запускать только нужные агенты и добавлять новые по мере роста бизнеса.</p>
        <div className="features-grid">
          {FEATURES.map((f) => {
            const s = STATUS_MAP[f.status];
            return (
              <div key={f.title} className="feature-card">
                <div className="feature-top">
                  <span className="feature-icon">{f.icon}</span>
                  <span className={`feature-status ${s.cls}`}>{s.label}</span>
                </div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="how" id="how">
        <div className="section-eyebrow">КАК ЭТО РАБОТАЕТ</div>
        <h2 className="section-title">Три принципа платформы</h2>
        <div className="how-grid">
          <div className="how-card"><div className="how-num">01</div><h3 className="how-title">Подключаем источники</h3><p className="how-desc">Bitrix24, TravelLine, Booking.com, банковские выписки — агенты читают данные откуда угодно через API или загрузку файлов.</p></div>
          <div className="how-card"><div className="how-num">02</div><h3 className="how-title">Агент анализирует</h3><p className="how-desc">Нейросети DeepSeek и Qwen обрабатывают данные локально и через облако. Простые задачи — бесплатно, сложный анализ — флагманские модели.</p></div>
          <div className="how-card"><div className="how-num">03</div><h3 className="how-title">Действует автономно</h3><p className="how-desc">Уведомления, отчёты, эскалации — всё происходит без участия человека. Вы получаете результат, а не задание.</p></div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-eyebrow">ГОТОВЫ НАЧАТЬ?</div>
          <h2 className="cta-title">Устраните ручной труд<br />уже сегодня</h2>
          <p className="cta-desc">Платформа разворачивается в вашей инфраструктуре. Данные остаются у вас.</p>
          <button className="btn-primary btn-large" onClick={() => setShowLogin(true)}>Войти в систему →</button>
        </div>
      </section>

      <footer className="footer">
        <div className="nav-logo"><span className="nav-logo-icon">⬡</span><span className="nav-logo-text">THOTH AI</span></div>
        <p className="footer-copy">© 2026 Thoth AI Platform. Автоматизация операций.</p>
      </footer>
    </div>
  );
}
